type ErrorKind =
  | "window_error"
  | "unhandled_rejection"
  | "react_render"
  | "api_error"
  | "network_error"
  | "manual";

type ErrorContext = {
  conversationId?: number | null;
  activeListingId?: number | null;
  extra?: Record<string, unknown>;
};

type ErrorReport = {
  kind: ErrorKind;
  name?: string | null;
  message?: string | null;
  stack?: string | null;
  source?: string | null;
  route?: string | null;
  url?: string | null;
  referrer?: string | null;
  session_id?: string | null;
  conversation_id?: number | null;
  active_listing_id?: number | null;
  build_version?: string | null;
  environment?: string | null;
  user_agent?: string | null;
  viewport_width?: number | null;
  viewport_height?: number | null;
  locale?: string | null;
  client_timestamp?: string | null;
  context?: Record<string, unknown> | null;
};

const STORAGE_KEY = "homeai_error_buffer_v1";
const SESSION_KEY = "homeai_error_session_v1";
const FLUSH_DEBOUNCE_MS = 1500;
const BATCH_LIMIT = 10;
const FINGERPRINT_TTL_MS = 60_000;
const MAX_BUFFER = 25;

const recentFingerprints = new Map<string, number>();
let pendingFlush: ReturnType<typeof setTimeout> | null = null;
let liveContext: ErrorContext = {};
let installed = false;

const isBrowser = () => typeof window !== "undefined";

function readSessionId(): string {
  if (!isBrowser()) return "";
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const fresh = `s_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    window.sessionStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch {
    return "";
  }
}

function readBuffer(): ErrorReport[] {
  if (!isBrowser()) return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(-MAX_BUFFER);
  } catch {
    return [];
  }
}

function writeBuffer(items: ErrorReport[]) {
  if (!isBrowser()) return;
  try {
    if (items.length === 0) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(-MAX_BUFFER)));
  } catch {
    // localStorage may be full or disabled — accept loss and continue.
  }
}

function resolveApiUrl(): string {
  const configured = process.env.NEXT_PUBLIC_API_URL?.trim();
  if (configured) return configured.replace(/\/$/, "");
  if (isBrowser()) {
    const hostname = window.location.hostname;
    if (
      hostname === "openhomesai.casa"
      || hostname === "www.openhomesai.casa"
      || hostname.endsWith(".vercel.app")
    ) {
      return "https://openhomesai-production.up.railway.app";
    }
  }
  return "http://localhost:8000";
}

function fingerprint(report: ErrorReport): string {
  const parts = [
    report.kind,
    (report.name || "").slice(0, 80),
    (report.message || "").slice(0, 200),
    (report.source || "").slice(0, 200),
    (report.stack || "").split("\n").slice(0, 3).join("|").slice(0, 600),
  ];
  return parts.join("::");
}

function shouldThrottle(report: ErrorReport): boolean {
  const key = fingerprint(report);
  const now = Date.now();
  recentFingerprints.forEach((ts, oldKey) => {
    if (now - ts > FINGERPRINT_TTL_MS) {
      recentFingerprints.delete(oldKey);
    }
  });
  const last = recentFingerprints.get(key);
  if (last && now - last < FINGERPRINT_TTL_MS) {
    return true;
  }
  recentFingerprints.set(key, now);
  return false;
}

function enrich(report: Partial<ErrorReport> & { kind: ErrorKind }): ErrorReport {
  const w = isBrowser() ? window : undefined;
  const buildVersion = process.env.NEXT_PUBLIC_BUILD_VERSION || null;
  const environment = process.env.NEXT_PUBLIC_VERCEL_ENV || process.env.NODE_ENV || null;
  return {
    kind: report.kind,
    name: report.name ?? null,
    message: report.message ?? null,
    stack: report.stack ?? null,
    source: report.source ?? null,
    route: report.route ?? (w?.location.pathname ?? null),
    url: report.url ?? (w?.location.href ?? null),
    referrer: report.referrer ?? (typeof document !== "undefined" ? document.referrer || null : null),
    session_id: report.session_id ?? readSessionId(),
    conversation_id: report.conversation_id ?? liveContext.conversationId ?? null,
    active_listing_id: report.active_listing_id ?? liveContext.activeListingId ?? null,
    build_version: report.build_version ?? buildVersion,
    environment: report.environment ?? (environment ? String(environment) : null),
    user_agent: report.user_agent ?? (typeof navigator !== "undefined" ? navigator.userAgent : null),
    viewport_width: report.viewport_width ?? w?.innerWidth ?? null,
    viewport_height: report.viewport_height ?? w?.innerHeight ?? null,
    locale: report.locale ?? (typeof navigator !== "undefined" ? navigator.language : null),
    client_timestamp: report.client_timestamp ?? new Date().toISOString(),
    context: { ...(liveContext.extra || {}), ...(report.context || {}) },
  };
}

async function sendBatch(items: ErrorReport[]): Promise<boolean> {
  if (items.length === 0) return true;
  const endpoint = `${resolveApiUrl()}/client-errors`;
  const payload = JSON.stringify({ errors: items });
  try {
    const response = await fetch(endpoint, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    });
    return response.ok;
  } catch {
    return false;
  }
}

function scheduleFlush(immediate = false) {
  if (!isBrowser()) return;
  if (pendingFlush) {
    clearTimeout(pendingFlush);
    pendingFlush = null;
  }
  const flush = async () => {
    pendingFlush = null;
    const buffer = readBuffer();
    if (buffer.length === 0) return;
    const batch = buffer.slice(0, BATCH_LIMIT);
    const remainder = buffer.slice(batch.length);
    const ok = await sendBatch(batch);
    if (ok) {
      writeBuffer(remainder);
      if (remainder.length > 0) {
        scheduleFlush(false);
      }
    }
  };
  if (immediate) {
    void flush();
  } else {
    pendingFlush = setTimeout(() => { void flush(); }, FLUSH_DEBOUNCE_MS);
  }
}

function flushViaBeacon() {
  if (!isBrowser() || typeof navigator === "undefined" || typeof navigator.sendBeacon !== "function") return;
  const buffer = readBuffer();
  if (buffer.length === 0) return;
  try {
    const endpoint = `${resolveApiUrl()}/client-errors`;
    const blob = new Blob([JSON.stringify({ errors: buffer.slice(0, BATCH_LIMIT) })], { type: "application/json" });
    const ok = navigator.sendBeacon(endpoint, blob);
    if (ok) writeBuffer([]);
  } catch {
    // best-effort
  }
}

export function reportError(input: Partial<ErrorReport> & { kind: ErrorKind }) {
  if (!isBrowser()) return;
  const report = enrich(input);
  if (shouldThrottle(report)) return;
  const buffer = readBuffer();
  buffer.push(report);
  writeBuffer(buffer);
  scheduleFlush();
}

export function setErrorContext(context: ErrorContext) {
  liveContext = { ...liveContext, ...context };
}

export function installErrorReporting() {
  if (!isBrowser() || installed) return;
  installed = true;

  window.addEventListener("error", (event) => {
    const errorObject = (event as ErrorEvent).error as Error | undefined;
    reportError({
      kind: "window_error",
      name: errorObject?.name || "Error",
      message: errorObject?.message || (event as ErrorEvent).message || "Unknown window error",
      stack: errorObject?.stack || null,
      source: (event as ErrorEvent).filename
        ? `${(event as ErrorEvent).filename}:${(event as ErrorEvent).lineno}:${(event as ErrorEvent).colno}`
        : null,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = (event as PromiseRejectionEvent).reason;
    const reasonError = reason instanceof Error ? reason : null;
    reportError({
      kind: "unhandled_rejection",
      name: reasonError?.name || "UnhandledRejection",
      message: reasonError?.message || (typeof reason === "string" ? reason : safeStringify(reason)),
      stack: reasonError?.stack || null,
    });
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushViaBeacon();
    }
  });

  window.addEventListener("pagehide", () => {
    flushViaBeacon();
  });

  scheduleFlush();
}

export function reportReactError(error: Error, info: { componentStack?: string | null }) {
  reportError({
    kind: "react_render",
    name: error.name,
    message: error.message,
    stack: error.stack || info?.componentStack || null,
    source: info?.componentStack ? "react_component_stack" : null,
    context: { componentStack: info?.componentStack || null },
  });
}

export function reportApiError(opts: {
  status?: number | null;
  url?: string | null;
  method?: string | null;
  message?: string | null;
  detail?: unknown;
}) {
  reportError({
    kind: opts.status === undefined || opts.status === null || opts.status === 0 ? "network_error" : "api_error",
    name: opts.status ? `API ${opts.status}` : "NetworkError",
    message: opts.message || (typeof opts.detail === "string" ? opts.detail : null) || "API request failed",
    source: opts.url ? `${opts.method || "GET"} ${opts.url}` : null,
    context: {
      status: opts.status ?? null,
      method: opts.method ?? null,
      endpoint: opts.url ?? null,
      detail: typeof opts.detail === "object" ? opts.detail : undefined,
    },
  });
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export type { ErrorKind, ErrorContext };
